import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { Trash2, ShieldAlert } from "lucide-react";
import { requestEntityDeletion } from "../../services/deletionRequestService";
import styles from "./DeleteConfirmationModal.module.css";

/**
 * Universal deletion confirmation modal with entity name verification and permission delegation.
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Closes modal
 * @param {string} entityType - e.g. "User", "Society", "Service", "Amenity", "Role", "Scope", "Tower"
 * @param {string|number} entityId - Identifier of entity
 * @param {string} entityName - Name of the entity to verify against
 * @param {boolean} canDelete - If false, user doesn't have delete access; shows request to delete flow
 * @param {function} onConfirm - Function to invoke on confirmed direct deletion
 * @param {function} onRequestDelete - Optional custom callback when deletion request is submitted
 * @param {string} title - Optional custom modal title
 * @param {string} description - Optional custom subtitle
 * @param {string} actionLabel - Button label (default "Delete" or "Deactivate")
 */
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  entityType = "Item",
  entityId,
  entityName = "",
  canDelete = true,
  onConfirm,
  onRequestDelete,
  title,
  description,
  actionLabel = "Delete",
}) => {
  const [verificationInput, setVerificationInput] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Normalize target verification text (fallback to "DELETE" if name is blank)
  const targetVerification = (entityName || "DELETE").trim();

  // Reset fields whenever modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setVerificationInput("");
      setReason("");
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Comparison: case-insensitive trimmed match
  const isVerified =
    verificationInput.trim().toLowerCase() === targetVerification.toLowerCase();

  // Handle direct deletion (user has permission)
  const handleExecuteDelete = async () => {
    if (!isVerified) return;
    try {
      setSubmitting(true);
      if (onConfirm) {
        await onConfirm();
      }
      onClose();
    } catch (err) {
      console.error("Direct deletion error:", err);
      toast.error(err?.message || `Failed to ${actionLabel.toLowerCase()} ${entityType}.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle deletion request (user has NO direct permission)
  const handleSubmitRequest = async () => {
    if (!isVerified || !reason.trim()) return;
    try {
      setSubmitting(true);
      if (onRequestDelete) {
        await onRequestDelete(reason.trim());
      } else {
        const res = await requestEntityDeletion({
          entity_type: entityType,
          entity_id: entityId,
          entity_name: targetVerification,
          reason: reason.trim(),
        });
        toast.success(
          res?.message ||
            `Deletion request for "${targetVerification}" sent to administrators and your supervisors.`
        );
      }
      onClose();
    } catch (err) {
      console.error("Deletion request error:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit deletion request.");
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle =
    title ||
    (canDelete
      ? `${actionLabel} ${entityType}`
      : `Request Deletion of ${entityType}`);

  return (
    <Modal isOpen={isOpen} onClose={submitting ? undefined : onClose} title={modalTitle}>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={canDelete ? styles.iconCircleDanger : styles.iconCircleWarning}>
            {canDelete ? <Trash2 size={22} /> : <ShieldAlert size={22} />}
          </div>
          <div>
            <h4 className={styles.titleText}>
              {canDelete
                ? `Confirm ${actionLabel.toLowerCase()} of ${entityType.toLowerCase()}`
                : `Submit Deletion Request for ${entityType.toLowerCase()}`}
            </h4>
            <p className={styles.description}>
              {description ||
                (canDelete
                  ? `This action will ${actionLabel.toLowerCase()} the entity and may permanently affect related records.`
                  : `You do not have direct permission to delete this ${entityType.toLowerCase()}. You can submit an official deletion request to administrators.`)}
            </p>
          </div>
        </div>

        {!canDelete && (
          <div className={styles.noAccessBanner}>
            <strong>Authorization Notice:</strong> Your request will be forwarded to the Super Admin and your supervising manager for review and approval.
          </div>
        )}

        <div className={styles.entityCard}>
          <span className={styles.entityLabel}>{entityType} to {canDelete ? actionLabel : "Request for Removal"}</span>
          <span className={styles.entityHighlight}>{targetVerification}</span>
          {entityId && (
            <span style={{ fontSize: "11px", color: "var(--text-muted, #6b7280)" }}>
              ID: {entityId}
            </span>
          )}
        </div>

        {!canDelete && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                marginBottom: "5px",
                color: "var(--text-color, #374151)",
              }}
            >
              Reason / Justification <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              className={styles.textareaField}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Explain why this ${entityType.toLowerCase()} should be removed or deactivated...`}
              disabled={submitting}
              required
            />
          </div>
        )}

        <div>
          <div className={styles.promptText}>
            To verify, please type <span className={canDelete ? styles.codeBadge : styles.codeBadgeWarning}>{targetVerification}</span> below:
          </div>
          <input
            type="text"
            className={[styles.inputField, !canDelete ? styles.inputFieldWarning : ""].filter(Boolean).join(" ")}
            value={verificationInput}
            onChange={(e) => setVerificationInput(e.target.value)}
            placeholder={targetVerification}
            disabled={submitting}
            autoFocus
          />
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>

          {canDelete ? (
            <Button
              type="button"
              variant="danger"
              onClick={handleExecuteDelete}
              disabled={!isVerified || submitting}
              loading={submitting}
            >
              {actionLabel} {entityType}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmitRequest}
              disabled={!isVerified || !reason.trim() || submitting}
              loading={submitting}
              style={{ backgroundColor: "#d97706", borderColor: "#b45309" }}
            >
              Submit Deletion Request
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmationModal;
